import { ApolloProvider } from '@apollo/client';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  initialize as initAnalytics,
  routeChangeHandler,
} from '@side/analytics';
import { SessionProvider } from '@side/next-auth/react';
import { AppErrorFallback, ToastProvider } from '@side/pantry';
import { APIProvider as MapsAPIProvider } from '@vis.gl/react-google-maps';
import { enableMapSet, enablePatches } from 'immer';
import { Provider as JotaiProvider } from 'jotai';
import Head from 'next/head';
import { type ReactElement, useCallback, useEffect } from 'react';
import type { SideAppProps } from 'src/common/types/app';
import { ErrorBoundary } from 'src/modules/errors';
import { createEmotionCache } from 'src/theme/createEmotionCache';
import { SWRConfig } from 'swr';
import { useDisableNativePinchZoom } from '@/common/hooks/useDisableNativePinchZoom';
import { AppRefreshModal } from '@/components/AppRefreshModal';
import { AppShell } from '@/components/AppShell';
import { ContactsIntegrationModal } from '@/components/ContactsIntegration';
import { Initialization } from '@/components/Initialization';
import { SetupMaze } from '@/components/Initialization/SetupMaze';
import { UnsavedChangesProvider } from '@/components/UnsavedChanges';
import { WithValidInteraction } from '@/components/WithValidInteraction';
import { LicensedStateContextProvider } from '@/contexts/LicensedStateContext';
import { initDatadog } from '@/modules/datadog/init-datadog';
import { theme } from '@/theme';
import { InteractionLockedProvider } from '@/views/Interactions/context/InteractionLockedContext';
import { AnalyticsIdentify } from '$analytics/AnalyticsIdentify';
import { shouldRetryOnError } from '$api-clients/swr/shouldRetryOnError';
import { withRetryStatus } from '$api-clients/swr/withRetryStatus';
import { client } from '$apollo-client';
import { WithFullUser } from '$authentication/WithFullUser';
import { LaunchDarklyProvider } from '$launch-darkly/provider';
import { PantryProvider } from '$pantry/PantryProvider';
import { UserTrackingProvider } from '$user-tracking';
import { GlobalStyles } from '../theme/globalStyles';

const clientSideEmotionCache = createEmotionCache();

enableMapSet();
enablePatches();

initAnalytics({ app: 'transactions-management-ui' });
initDatadog();

export default function SideApp(props: SideAppProps): ReactElement {
  const {
    Component,
    emotionCache = clientSideEmotionCache,
    pageProps,
    router,
  } = props;

  useDisableNativePinchZoom();

  useEffect(() => {
    router.events.on('routeChangeComplete', routeChangeHandler);
    return () => {
      router.events.off('routeChangeComplete', routeChangeHandler);
    };
  }, [router]);

  const {
    noAppShell = false,
    appShellProps = {},
    auth,
    disableInteractionValidation = false,
  } = Component;

  const pageContent = disableInteractionValidation ? (
    <Component {...pageProps} />
  ) : (
    <WithValidInteraction>
      <Component {...pageProps} />
    </WithValidInteraction>
  );

  const content = (
    <LaunchDarklyProvider>
      <WithFullUser>
        <GlobalStyles />
        <ThemeProvider theme={theme}>
          <PantryProvider>
            <UnsavedChangesProvider>
              <LicensedStateContextProvider>
                <UserTrackingProvider>
                  <Initialization />
                  <InteractionLockedProvider>
                    {!noAppShell ? (
                      <AppShell {...appShellProps}>{pageContent}</AppShell>
                    ) : (
                      pageContent
                    )}
                  </InteractionLockedProvider>
                  <AnalyticsIdentify />
                </UserTrackingProvider>
              </LicensedStateContextProvider>
            </UnsavedChangesProvider>
            <AppRefreshModal />
            <ContactsIntegrationModal />
          </PantryProvider>
        </ThemeProvider>
      </WithFullUser>
    </LaunchDarklyProvider>
  );
  const fallbackFunc = useCallback(
    ({ error }: { error: Error }) => (
      <AppErrorFallback error={error} showErrorText={false} />
    ),
    [],
  );

  return (
    <ErrorBoundary fallback={fallbackFunc}>
      <SWRConfig
        value={{
          errorRetryCount: 3,
          shouldRetryOnError,
          use: [withRetryStatus],
        }}
      >
        <SetupMaze />
        <SessionProvider
          pageOptions={auth}
          basePath={process.env.NEXT_PUBLIC_AUTH_BASE_PATH}
        >
          <ApolloProvider client={client}>
            <CacheProvider value={emotionCache}>
              <Head>
                <title>Side</title>
                <meta
                  name="viewport"
                  content="minimum-scale=1, initial-scale=1, width=device-width, maximum-scale=1"
                />
              </Head>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <JotaiProvider>
                  <CssBaseline />
                  <MapsAPIProvider
                    apiKey={process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY ?? ''}
                  >
                    {content}
                  </MapsAPIProvider>
                </JotaiProvider>
              </LocalizationProvider>
            </CacheProvider>
            <ToastProvider />
          </ApolloProvider>
        </SessionProvider>
      </SWRConfig>
    </ErrorBoundary>
  );
}
